// useSectionItem.ts
import { useState } from "react";
import {
  FieldError,
  useFieldArray,
  Control,
  UseFormSetValue,
  UseFormGetValues,
  UseFormSetError,
  FieldErrors,
  UseFieldArrayAppend,
} from "react-hook-form";
import { useToggle } from "@/lib/hooks";
import { sectionSchema } from "@/hooks/menu/useCreateMenu";
import { ValidationError } from "yup";
import {
  useAddEditSectionMutation,
  useDuplicateSectionMutation,
  useDeleteSectionMutation,
} from "@/redux/services/menu/menu.api";
import toast from "react-hot-toast";
import {
  MenuFormData,
  Section,
  SectionFormData,
} from "@/redux/services/menu/menu.type";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { createElement } from "react";
import { Trash2 } from "lucide-react";
import { useUploadMedia } from "@/lib/useUploadMedia";
import {
  convertPriceToCent,
  getMenuItemOptionValue,
  isValidUploadedImage,
} from "@/lib/utils";
import { toastApiError } from "@/lib/apiErrors";
interface UseSectionItemProps {
  index: number;
  control: Control<MenuFormData>;
  getValues: UseFormGetValues<MenuFormData>;
  setValue: UseFormSetValue<MenuFormData>;
  setError: UseFormSetError<MenuFormData>;
  section?: Section;
  isEdit?: boolean;
  errors: FieldErrors<MenuFormData>;
  menuId?: string;
  removeSection: (index: number) => void;
  appendSection: UseFieldArrayAppend<MenuFormData, "menuSections">;
}

export const useSectionItem = ({
  index,
  control,
  getValues,
  setError,
  errors,
  menuId,
  removeSection,
  appendSection,
  setValue,
}: UseSectionItemProps) => {
  const [imageSection, setImageSection] = useState<string>("");
  const confirm = useConfirm();
  const [duplicate, { isLoading: isLoadDupplicateSectionItem }] =
    useDuplicateSectionMutation();
  const [deleteSection, { isLoading: isLoadDeleteSectionItem }] =
    useDeleteSectionMutation();
  const { isOpen: isShow, open, close } = useToggle();
  const [selectedItemIndex, setSelectedItemIndex] = useState<number | null>(
    null
  );

  const sectionItem = getValues(`menuSections.${index}`);

  const sectionErrors = errors.menuSections?.[index];

  const openModal = (itemIndex: number) => {
    setSelectedItemIndex(itemIndex);
    open();
  };

  const openEditModal = (itemIndex: number) => {
    setSelectedItemIndex(itemIndex);
    open();
  };

  const {
    fields: menuItemsFields,
    append: appendMenuItem,
    remove: removeMenuItem,
  } = useFieldArray({
    control,
    name: `menuSections.${index}.menuItems` as const,
  });

  // Handle image selection for a section
  const { uploadMedia } = useUploadMedia();
  const onSelectSectionImage = async (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    if (e.currentTarget.files) {
      const file = e.currentTarget.files[0];
      setImageSection(URL.createObjectURL(file));
      const media = await uploadMedia(file);
      if (media && "id" in media) {
        setValue(
          `menuSections.${index}.image`,
          `/api/media_objects/${media?.id}`
        );
      }
    }
  };

  // Function to duplicate a section
  const handleDuplicateSectionItem = (
    sectionIndex: number,
    sectionId?: string
  ) => {
    if (sectionId) {
      duplicate(sectionId);
    } else {
      const itemToDuplicate = getValues(`menuSections.${sectionIndex}`);
      appendSection({
        ...itemToDuplicate,
      });
    }
  };

  // Function to remove a section
  const handleRemoveSectionItem = async (
    sectionIndex: number,
    sectionId?: string
  ) => {
    if (sectionId) {
      const ok = await confirm({
        title: "Êtes-vous sûr de vouloir supprimer cette section ?",
        description: "Vous ne pourrez pas revenir en arrière !",
        confirmText: "Supprimer",
        cancelText: "Annuler",
        variant: "destructive",
        icon: createElement(Trash2, { className: "h-6 w-6" }),
      });
      if (!ok) return;
      const res = await deleteSection(sectionId);
      if ("data" in res) {
        toast.success("Section supprimée avec succès");
      } else {
        toastApiError(
          res.error,
          "Une erreur est survenue lors de la suppression de la section"
        );
      }
    } else {
      removeSection(sectionIndex);
    }
  };

  const [addEditSection, { isLoading: isLoadingToAddOrEditSection }] =
    useAddEditSectionMutation();

  const handleAddEditSection = async (
    sectionIndex: number,
    sectionId?: string
  ) => {
    const sectionData = getValues(`menuSections.${sectionIndex}`);
    sectionSchema
      .validate(sectionData, { abortEarly: false })
      .then(async () => {
        const { image, ...rest } = sectionData;
        const dataToSend: SectionFormData = {
          ...rest,
          sortOrder: Number(sectionData.sortOrder),
          menuItems: sectionData?.menuItems?.map((menuItem) => ({
            ...menuItem,
            image: isValidUploadedImage(menuItem.image)
              ? menuItem.image
              : undefined,
            sortOrder: Number(menuItem.sortOrder),
            basePrice: convertPriceToCent(menuItem.basePrice),
            itemOptions: sectionId
              ? menuItem.itemOptions && menuItem?.itemOptions?.length > 0
                ? menuItem?.itemOptions?.map((itemOption: any) => {
                    return {
                      menuItemOption: `/api/menu_item_options/${getMenuItemOptionValue(
                        itemOption
                      )}`,
                    };
                  })
                : []
              : menuItem?.itemOptions,
          })),
        };
        if (isValidUploadedImage(image)) {
          dataToSend["image"] = image;
        }

        if (menuId) {
          dataToSend["menu"] = `api/menus/${menuId}`;
        }

        const res = await addEditSection({
          id: sectionId,
          data: dataToSend,
        });
        if ("data" in res) {
          toast.success(
            `Section ${sectionId ? "mise à jour" : "ajouté"} avec succès`
          );
        } else {
          toastApiError(
            res.error,
            `Une erreur est survenue lors de ${
              sectionId ? "la mise à jour" : "l'ajout"
            } de la section`
          );
        }
      })
      .catch((err: ValidationError) => {
        err.inner.forEach((validationError) => {
          const path = validationError.path as keyof MenuFormData;
          const errorMessages = validationError.errors as string[];
          // @ts-ignore
          setError(`menuSections.${sectionIndex}.${path}`, {
            type: "manual",
            message: errorMessages[0],
          } as FieldError);
        });
      });
  };

  return {
    isShow,
    openModal,
    close,
    selectedItemIndex,
    menuItemsFields,
    appendMenuItem,
    handleAddEditSection,
    sectionItem,
    sectionErrors,
    openEditModal,
    setSelectedItemIndex,
    isLoadingToAddOrEditSection,
    isLoadDupplicateSectionItem,
    isLoadDeleteSectionItem,
    handleDuplicateSectionItem,
    handleRemoveSectionItem,
    removeMenuItem,
    onSelectSectionImage,
    imageSection,
  };
};
